import React from 'react';
import { createPortal } from 'react-dom';
import pt from 'prop-types';
import startCase from 'lodash/startCase';
import kebabCase from 'lodash/kebabCase';

import { Field } from 'components/form';
import { ModalsContext } from 'client/src/context/modals';
import { WidgetContext } from 'components/storefront/editor/widgets';
import SwellHeader from 'components/storefront/editor/widgets/header';
import SidebarPanel from 'components/storefront/editor/widgets/sidebar-panel';

import SwellColorSchemeValue from './color-value';
import SwellColorPanelFooterActions from './color-panel-footer-actions';
import SwellColorSchemePreview from './color-scheme-preview';

export const MODAL_PROCESS_SCHEME_ID = 'editor_process_color_scheme';

function runAfterLifecycleMethod(lifecycle, method, callback) {
  if (typeof lifecycle?.[method] === 'function') {
    lifecycle[method]().then(callback);
  } else {
    callback();
  }
}

function SwellColorSchemePanel({
  selected,
  value,
  fields,
  onSchemeChange,
  onSchemeDuplicate,
  onSchemeDelete,
  onPanelClose,
}) {
  const modalsCtx = React.useContext(ModalsContext);
  const { notifySuccess } = React.useContext(WidgetContext);
  const panelLifecycleRef = React.useRef(null);

  // Here we need to use useMemo instead of useEffect
  React.useMemo(() => {
    if (selected) {
      panelLifecycleRef.current = null;
    }
  }, [selected]);

  /** @type {React.FocusEventHandler<HTMLInputElement>} */
  const onSchemeRename = React.useCallback(
    (event) => {
      onSchemeChange({ label: event.currentTarget.value });
    },
    [onSchemeChange],
  );

  const onSchemeDuplicateSubmit = React.useCallback(
    (values) => {
      const schemeId = kebabCase(values.name);

      const updatedValue = {
        ...value,
        [schemeId]: {
          ...value[values.id],
          settings: {
            ...value[values.id].settings,
            label: values.name,
          },
        },
      };

      modalsCtx.closeModal(MODAL_PROCESS_SCHEME_ID);

      runAfterLifecycleMethod(panelLifecycleRef.current, 'close', () => {
        onSchemeDuplicate(schemeId, updatedValue);
      });

      notifySuccess(`Created "${values.name}" color scheme`);
    },
    [value, onSchemeDuplicate, notifySuccess, modalsCtx],
  );

  const onDuplicateScheme = React.useCallback(() => {
    let schemeId = `${selected}-copy`;

    if (Object.hasOwn(value, schemeId)) {
      for (let index = 1; ; ++index) {
        const schemeIdWithIndex = `${schemeId}-${index}`;

        if (!Object.hasOwn(value, schemeIdWithIndex)) {
          schemeId = schemeIdWithIndex;
          break;
        }
      }
    }

    onSchemeDuplicateSubmit({
      id: selected,
      name: startCase(schemeId),
    });
  }, [selected, value, onSchemeDuplicateSubmit]);

  const onDeleteScheme = React.useCallback(() => {
    modalsCtx.openModal('Confirm', {
      title: 'Delete color scheme',
      message: (
        <p>
          Are you sure you want to delete <b>{startCase(selected)}</b> color
          scheme?
        </p>
      ),
      action: 'Delete',
      actionType: 'danger',
      onConfirm() {
        runAfterLifecycleMethod(panelLifecycleRef.current, 'close', () => {
          const updatedValue = { ...value };

          delete updatedValue[selected];

          onSchemeDelete(updatedValue);
        });
      },
    });
  }, [selected, value, onSchemeDelete, modalsCtx]);

  const onCancel = React.useCallback(
    (initialValue) => {
      const { activeElement } = document;

      if (typeof activeElement?.blur === 'function') {
        // Prevent aria-hidden warning
        activeElement.blur();
      }

      onSchemeChange(initialValue);
    },
    [onSchemeChange],
  );

  const settings = React.useMemo(() => {
    return value?.[selected]?.settings || null;
  }, [selected, value]);

  const schemeLabel = React.useMemo(() => {
    return settings?.label || startCase(selected);
  }, [selected, settings]);

  const presetColors = React.useMemo(() => {
    const colors = new Set();

    for (const field of fields) {
      const value = settings?.[field.id];

      if (value) {
        colors.add(value);
      }
    }

    return Array.from(colors);
  }, [settings, fields]);

  if (!settings) {
    return null;
  }

  return createPortal(
    <SidebarPanel
      title="Colors"
      subtitle={schemeLabel}
      value={settings}
      lifecycleRef={panelLifecycleRef}
      footerActions={
        <SwellColorPanelFooterActions
          onDuplicate={onDuplicateScheme}
          onDelete={onDeleteScheme}
        />
      }
      onClose={onPanelClose}
      onCancel={onCancel}
    >
      <div className="storefront-editor-widget">
        <Field
          type="text"
          name="label"
          label="Scheme name"
          defaultValue={schemeLabel}
          onBlur={onSchemeRename}
        />
      </div>

      <SwellColorSchemePreview
        schemeId={selected}
        settings={settings}
        className="storefront-editor--color-scheme--preview-field"
      />

      {fields.map((field, index) => {
        const { id, label, type } = field;

        if (type === 'header') {
          return <SwellHeader key={id || index} field={field} />;
        }

        const value = settings[id];

        return (
          <SwellColorSchemeValue
            key={id}
            path={id}
            type={type}
            label={label}
            value={value}
            presetColors={presetColors}
            onChange={onSchemeChange}
          />
        );
      })}
    </SidebarPanel>,
    document.getElementById('sidebar-panels-root'),
  );
}

SwellColorSchemePanel.propTypes = {
  selected: pt.string.isRequired,
  value: pt.object.isRequired,
  fields: pt.array.isRequired,
  onSchemeChange: pt.func.isRequired,
  onSchemeDuplicate: pt.func.isRequired,
  onSchemeDelete: pt.func.isRequired,
  onPanelClose: pt.func.isRequired,
};

export default React.memo(SwellColorSchemePanel);
